import moment from 'moment';
import { DATE_TIME_FORMAT, SESSION_KEY_CLIENT_ID, SESSION_KEY_GROUP_ID, SESSION_KEY_LOCATION_ID, SESSION_KEY_USER_ORDERS_DATA, SESSION_KEY_UTM_PARAMS } from '../constants';
import { CartItem, EventDetail, PricingDiscountTypes } from '../types';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween'

export function calculateAge(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

export function calculateAgeInMonths(dob: string): number {
  const birthDate = dayjs(dob);
  const now = dayjs();
  return now.diff(birthDate, 'month', true);
}

export function getClientId() {
  const urlParams = new URLSearchParams(window.location.search);
  const clientIdFromUrl = urlParams.get(SESSION_KEY_CLIENT_ID);

  if (clientIdFromUrl) {
    // If new Client ID, then we clear the session
    if (clientIdFromUrl != localStorage.getItem(SESSION_KEY_CLIENT_ID)) {
      localStorage.clear();
    }

    localStorage.setItem(SESSION_KEY_CLIENT_ID, clientIdFromUrl);
    return clientIdFromUrl;
  } else {
    const clientIdFromStorage = localStorage.getItem(SESSION_KEY_CLIENT_ID);
    if (!clientIdFromStorage && !window.location.pathname.includes('kids-co')) {
      window.location.href = 'https://kindertales.com';
    }

    return clientIdFromStorage;
  }
}

export function getGroupId(): number {
  const urlParams = new URLSearchParams(window.location.search);
  const groupIdFromUrl = urlParams.get(SESSION_KEY_GROUP_ID);

  if (groupIdFromUrl) {
    localStorage.setItem(SESSION_KEY_GROUP_ID, groupIdFromUrl);
    return groupIdFromUrl as unknown as number;
  }
  return 0;
}

export function getLocationId() {
  const urlParams = new URLSearchParams(window.location.search);
  const locationIdFromUrl = urlParams.get(SESSION_KEY_LOCATION_ID);

  if (locationIdFromUrl) {
    // If new Location ID, then we clear the session
    if (locationIdFromUrl != localStorage.getItem(SESSION_KEY_LOCATION_ID)) {
      const clientIdFromStorage = localStorage.getItem(SESSION_KEY_CLIENT_ID) as string;
      localStorage.clear();
      localStorage.setItem(SESSION_KEY_CLIENT_ID, clientIdFromStorage);
    }

    localStorage.setItem(SESSION_KEY_LOCATION_ID, locationIdFromUrl);
    return locationIdFromUrl;
  } else {
    const locationIdFromStorage = localStorage.getItem(SESSION_KEY_LOCATION_ID);

    if (!locationIdFromStorage && !window.location.pathname.includes('kids-co')) {
      window.location.href = 'https://kindertales.com';
    }

    return locationIdFromStorage;
  }
}

export function convertTime(timeString: string) {
  return moment(timeString, 'HH:mm').format('h:mm A');
};

export const calculateDiscountedPrice = (
  chargePerPeriod: number | string | undefined,
  discountType: PricingDiscountTypes | undefined,
  discountAmount: number | string | undefined,
  couponDiscountAmount: number | string | undefined
): string => {
  let charge = parseFloat(chargePerPeriod ? chargePerPeriod.toString() : '0');
  const discount = parseFloat(discountAmount ? discountAmount.toString() : '0');
  const couponDiscount = parseFloat(couponDiscountAmount ? couponDiscountAmount.toString() : '0');

  if (couponDiscountAmount) {
    charge = charge - couponDiscount;
  }

  let finalPrice = charge;
  if (discountType === PricingDiscountTypes.FIXED) {
    finalPrice = charge - discount;
  } else if (discountType === PricingDiscountTypes.PERCENTAGE) {
    finalPrice = charge * (1 - discount / 100);
  }

  if (finalPrice < 0) {
    finalPrice = 0;
  }

  return finalPrice.toFixed(2);
};

export const getEventStatus = (event: EventDetail, childId: number | undefined) => {
  const currentDate = new Date();
  const startDate = new Date(event.registrationStartDate);
  const endDate = new Date(event.registrationEndDate);

  if (event.totalOccupiedSeats >= event.capacity) {
    return { status: 'Closed', className: 'closed' };
  }

  // Check if the child is registered for this event
  const userOrdersData = localStorage.getItem(SESSION_KEY_USER_ORDERS_DATA);
  if (childId && userOrdersData) {
      const orders = JSON.parse(userOrdersData);

      if (orders[childId] && orders[childId].includes(event.id)) {
          return { status: 'Registered', className: 'registered' };
      }
  }

  const isProgramEnrollmentExist = event.programEnrollments?.find(p => p.childId === childId);

  if (isProgramEnrollmentExist) {
    return { status: 'Registered', className: 'registered' };
  }

  if (currentDate < startDate) {
      return { status: 'Opening Soon', className: 'opening-soon' };
  } if (currentDate > endDate) {
      return { status: 'Closed', className: 'closed' };
  }
  return { status: 'Open', className: 'open' };
};

export const calculateTotalCouponDiscount = (items: any): number => {
  if (!items || !items.data || !Array.isArray(items.data)) return 0;
  
  return items.data.reduce((acc: any, item: { discountAmountCoupon: number; }) => {
    const discount = typeof item.discountAmountCoupon === 'number' 
      ? item.discountAmountCoupon 
      : parseFloat(item.discountAmountCoupon || '0');
    return acc + (isNaN(discount) ? 0 : discount);
  }, 0);
};
export function isValidPassword(str: string): boolean
{
  let re = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;
  return re.test(str);
}

export const formatDate = (date: string, format?: string) => {
  return dayjs(date, DATE_TIME_FORMAT).format(format ?? 'M/D/YYYY');
};

export const getChildrenInMultipleEventsAtTheSameTime = (cartItem: CartItem[]) => {
  dayjs.extend(isBetween)

  let childrenEvents: { [key: number]: CartItem[] } = {};
  let childrenWithEventsAtTheSameTime: { [key: number]: CartItem[] } = {};

  cartItem.forEach(item => {
    if (childrenEvents[item.child.id]) {
      if (childrenEvents[item.child.id].length) {
        childrenEvents[item.child.id].forEach(childItem => {
          let childItemStartDate = dayjs(childItem.item.event.startDate, 'YYYY-MM-DD');
          let childItemEndDate = dayjs(childItem.item.event.endDate, 'YYYY-MM-DD');
          let cartItemStartDate = dayjs(item.item.event.startDate, 'YYYY-MM-DD');
          let cartItemEndDate = dayjs(item.item.event.endDate, 'YYYY-MM-DD');
          if (childItemStartDate.isBetween(cartItemStartDate, cartItemEndDate, 'days', '[]') || childItemEndDate.isBetween(cartItemStartDate, cartItemEndDate, 'days', '[]')) {
            if (childrenWithEventsAtTheSameTime[item.child.id]) {
              childrenWithEventsAtTheSameTime[item.child.id] = [...childrenEvents[item.child.id], item];
            } else {
              childrenWithEventsAtTheSameTime[item.child.id] = [...childrenEvents[item.child.id], item];
            }
          }
        })
        childrenEvents[item.child.id].push(item);
      }
    } else {
      childrenEvents[item.child.id] = [item];
    }
  })

  return childrenWithEventsAtTheSameTime;
}

export const capitalizeFirstLetter = (str: string) => {
  if (str.length === 0) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Tracks and saves UTM parameters from the URL to localStorage.
 * Updates the stored UTM params if they have changed.
 * @returns The UTM parameters object, or null if no UTM params are present
 */
export function trackUtmParams(): Record<string, string> | null {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: Record<string, string> = {};

  // Extract all parameters that start with 'utm_'
  urlParams.forEach((value, key) => {
    if (key.toLowerCase().startsWith('utm_')) {
      utmParams[key.toLowerCase()] = value;
    }
  });

  // If no UTM params found, return null
  if (Object.keys(utmParams).length === 0) {
    return null;
  }

  // Get existing UTM params from localStorage
  const existingUtmParamsStr = localStorage.getItem(SESSION_KEY_UTM_PARAMS);
  let existingUtmParams: Record<string, string> | null = null;

  if (existingUtmParamsStr) {
    try {
      existingUtmParams = JSON.parse(existingUtmParamsStr);
    } catch (e) {
      // If parsing fails, treat as if no existing params
      existingUtmParams = null;
    }
  }

  // Check if UTM params have changed
  const hasChanged = !existingUtmParams || 
    JSON.stringify(existingUtmParams) !== JSON.stringify(utmParams);

  // Save to localStorage if changed or if no existing params
  if (hasChanged) {
    localStorage.setItem(SESSION_KEY_UTM_PARAMS, JSON.stringify(utmParams));
  }

  return utmParams;
}

/**
 * Retrieves UTM parameters from localStorage.
 * @returns The UTM parameters object, or null if not found
 */
export function getUtmParams(): Record<string, string> | null {
  const utmParamsStr = localStorage.getItem(SESSION_KEY_UTM_PARAMS);
  
  if (!utmParamsStr) {
    return null;
  }

  try {
    return JSON.parse(utmParamsStr);
  } catch (e) {
    return null;
  }
}
